import { useEffect, useState } from 'react';
import { Modal, Form, Input, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	createBouwheer,
	updateBouwheer,
	Bouwheer,
	bouwheerKeys,
} from '@/libs/fleet-api/services/admin-bouwheer';

interface BouwheerFormModalProps {
	open: boolean;
	onClose: () => void;
	mode: 'create' | 'update';
	initialValues?: Bouwheer;
}

const BouwheerFormModal = ({
	open,
	onClose,
	mode,
	initialValues,
}: BouwheerFormModalProps) => {
	const [form] = Form.useForm();
	const queryClient = useQueryClient();
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (open) {
			if (mode === 'update' && initialValues) {
				form.setFieldsValue({
					id: initialValues.id,
					name: initialValues.name,
					type: initialValues.type || 'Company',
					industrySectorCode: initialValues.industrySector?.code,
				});
			} else {
				form.resetFields();
				form.setFieldsValue({
					type: 'Company',
				});
			}
		}
	}, [open, initialValues, mode, form]);

	const createMutation = useMutation({
		mutationFn: createBouwheer,
		onSuccess: () => {
			message.success('Bouwheer created successfully');
			queryClient.invalidateQueries({ queryKey: bouwheerKeys.all });
			onClose();
		},
		onError: (error) => {
			message.error('Failed to create Bouwheer');
			console.error(error);
		},
	});

	const updateMutation = useMutation({
		mutationFn: updateBouwheer,
		onSuccess: () => {
			message.success('Bouwheer updated successfully');
			queryClient.invalidateQueries({ queryKey: bouwheerKeys.all });
			onClose();
		},
		onError: (error) => {
			message.error('Failed to update Bouwheer');
			console.error(error);
		},
	});

	const handleSubmit = async () => {
		try {
			setIsSubmitting(true);
			const values = await form.validateFields();

			if (mode === 'create') {
				await createMutation.mutateAsync(values);
			} else {
				await updateMutation.mutateAsync({
					...values,
					id: initialValues?.id, // Ensure ID is passed for update if it's not in the form or disabled
				});
			}
		} catch (error) {
			console.error('Validation failed:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			title={mode === 'create' ? 'Add Bouwheer' : 'Edit Bouwheer'}
			open={open}
			onCancel={onClose}
			onOk={handleSubmit}
			confirmLoading={isSubmitting}
			destroyOnClose
		>
			<Form
				form={form}
				layout="vertical"
			>
				<Form.Item
					name="id"
					label="ID"
					rules={[{ required: true, message: 'Please enter ID' }]}
				>
					<Input disabled={mode === 'update'} />
				</Form.Item>
				<Form.Item
					name="name"
					label="Name"
					rules={[{ required: true, message: 'Please enter Name' }]}
				>
					<Input />
				</Form.Item>
				<Form.Item
					name="type"
					label="Type"
					rules={[{ required: true, message: 'Please enter Type' }]}
				>
					<Input disabled />
				</Form.Item>
				<Form.Item
					name="industrySectorCode"
					label="Industry Sector Code"
					rules={[
						{ required: true, message: 'Please enter Industry Sector Code' },
					]}
				>
					<Input />
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default BouwheerFormModal;
